import React from "react";
import { Link } from "react-router-dom";
import { Crown, Wine, Check, ArrowRight, Utensils } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { IMAGES } from "@/lib/content";

const PACKAGES = [
  {
    name: "VIP Section",
    price: "$100",
    unit: "deposit",
    icon: Crown,
    desc: "Reserve your own VIP section. Deposit secures the table — balance applied to your tab at the venue.",
    perks: ["Reserved VIP section", "Skip-the-line entry", "Dedicated server", "Balance due at venue"],
  },
  {
    name: "Premium Bottle Deal",
    price: "$300",
    unit: "all-in · $100 deposit",
    icon: Wine,
    featured: true,
    desc: "Choose your bottle: Casamigos, Patron, Hennessy, Tito's or Jameson — everything included for one price.",
    perks: ["Premium bottle of choice", "Mixers & garnishes", "Reserved VIP section", "$100 deposit to reserve"],
  },
];

export default function VIPTables() {
  return (
    <>
      <Seo title="VIP Tables & Bottle Service DC" description="Reserve VIP bottle service at ChiCha Lounge DC. VIP Section from $100 deposit and $300 all-in premium bottle deals (Casamigos, Patron, Hennessy, Tito's, Jameson) on U Street." path="/vip-tables" image={IMAGES.bottle} />
      <PageHero badge="Bottle Service" title="VIP Tables" subtitle="Reserved sections and premium bottle deals — the ultimate way to experience ChiCha nightlife." />

      <section className="pb-16">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8">
          <Stagger className="grid md:grid-cols-2 gap-6">
            {PACKAGES.map((p) => {
              const Icon = p.icon;
              return (
                <StaggerItem key={p.name}>
                  <div className={`card-lux p-8 h-full relative ${p.featured ? "!border-neutral-900" : ""}`}>
                    {p.featured && <span className="pill-badge absolute -top-3 left-8 !bg-neutral-900 !text-white !border-neutral-900">Most Popular</span>}
                    <span className="icon-square"><Icon size={22} /></span>
                    <h3 className="display-mega text-3xl mt-5">{p.name}</h3>
                    <div className="flex items-end gap-2 my-4">
                      <span className="display-mega text-5xl">{p.price}</span>
                      <span className="text-neutral-400 text-sm mb-1">{p.unit}</span>
                    </div>
                    <p className="text-neutral-500 mb-6">{p.desc}</p>
                    <ul className="space-y-3 mb-8">
                      {p.perks.map((perk) => <li key={perk} className="flex items-start gap-2 text-sm text-neutral-600"><Check size={16} className="text-neutral-900 shrink-0 mt-0.5" /> {perk}</li>)}
                    </ul>
                    <Link to="/buy-tickets" data-testid={`vip-book-${p.name.replace(/\s+/g, "-").toLowerCase()}`} className="btn-gold w-full justify-center">Reserve VIP <ArrowRight size={16} /></Link>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>

          <Reveal className="mt-6">
            <div className="card-lux p-8 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <span className="icon-square"><Utensils size={22} /></span>
                <div>
                  <h3 className="display-mega text-2xl">Dinner Reservations</h3>
                  <p className="text-neutral-500 mt-1">Dining is <span className="text-neutral-900 font-semibold">free before 10PM</span> — reserve your table in seconds.</p>
                </div>
              </div>
              <Link to="/reservations" className="btn-soft">Book a Table <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1300px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal className="relative rounded-[2rem] overflow-hidden min-h-[380px]">
            <img src={IMAGES.partyGirls} alt="VIP nightlife at ChiCha Lounge DC" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          </Reveal>
          <div>
            <h2 className="display-mega text-4xl md:text-5xl">Celebrate like a VIP</h2>
            <p className="text-neutral-500 text-lg mt-5 leading-relaxed">Birthday, bachelor party or a night out with the crew — our VIP hosts curate every detail, from bottle selection to the perfect hookah lineup.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/buy-tickets" className="btn-gold">Reserve VIP <ArrowRight size={16} /></Link>
              <Link to="/private-events" className="btn-ghost">Private Buyouts</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
